import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Country } from '@libs/core';
import { shopCategoryQueryOptions } from '@/hooks/queries/shopCategoryQuery';
import type { ShopCategoryForm, ShopCategoryMarket } from '@/types';
import { createEmptyAttributeCriteriaGroup, parseShopCriteriaToFormValues } from '@/utils/attributeCriteria';
import { formatShopCategoryDate } from '@/utils/formatShopCategoryDate';
import { ShopCategoryApiError, ShopCategoryNotFoundError } from '@/utils/shopCategoryApi';
const LOOKUP_DEBOUNCE_MS = 200;
const getCategoryKeyMarket = (categoryKey: string): ShopCategoryMarket | null => {
  const normalizedCategoryKey = categoryKey.trim().toUpperCase();
  if (normalizedCategoryKey.endsWith(`-${Country.US}`)) {
    return Country.US;
  }
  if (normalizedCategoryKey.endsWith(`-${Country.CA}`)) {
    return Country.CA;
  }
  return null;
};
export const useShopCategoryLookup = () => {
  const $ = _c(32);
  const {
    control,
    setValue
  } = useFormContext();
  const categoryKey = useWatch({
    control,
    name: "categoryKey"
  }) ?? "";
  let t0;
  if ($[0] !== categoryKey) {
    t0 = categoryKey.trim();
    $[0] = categoryKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const trimmedCategoryKey = t0;
  const [debouncedCategoryKey, setDebouncedCategoryKey] = useState(trimmedCategoryKey);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = debounce(setDebouncedCategoryKey, LOOKUP_DEBOUNCE_MS);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const debouncedSetCategoryKey = t1;
  let t2;
  let t3;
  if ($[3] !== trimmedCategoryKey) {
    t2 = () => {
      debouncedSetCategoryKey(trimmedCategoryKey);
      return () => debouncedSetCategoryKey.cancel();
    };
    t3 = [trimmedCategoryKey, debouncedSetCategoryKey];
    $[3] = trimmedCategoryKey;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== debouncedCategoryKey) {
    t4 = shopCategoryQueryOptions(debouncedCategoryKey);
    $[6] = debouncedCategoryKey;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const shopCategoryQuery = useQuery(t4);
  const isLookupSettled = debouncedCategoryKey === trimmedCategoryKey;
  let t5;
  let t6;
  if ($[8] !== debouncedCategoryKey || $[9] !== isLookupSettled || $[10] !== setValue || $[11] !== shopCategoryQuery.data || $[12] !== shopCategoryQuery.isError || $[13] !== shopCategoryQuery.isSuccess) {
    t5 = () => {
      const resetCriteriaFields = () => {
        setValue("attributeCriteriaGroups", [createEmptyAttributeCriteriaGroup()]);
        setValue("includedWebstyles", "");
        setValue("excludedWebstyles", "");
      };
      const clearDerivedFields = () => {
        setValue("categoryPath", "");
        setValue("market", null);
        setValue("url", "");
        setValue("startDate", "");
        setValue("endDate", "");
        resetCriteriaFields();
      };
      if (!isLookupSettled) {
        clearDerivedFields();
        return;
      }
      if (!debouncedCategoryKey || shopCategoryQuery.isError || shopCategoryQuery.isSuccess && !shopCategoryQuery.data?.automatedShop) {
        clearDerivedFields();
        return;
      }
      if (!shopCategoryQuery.isSuccess || !shopCategoryQuery.data) {
        return;
      }
      const {
        categoryKey: responseCategoryKey,
        categoryPath,
        market,
        url,
        startDate,
        endDate,
        shopCriteria
      } = shopCategoryQuery.data;
      setValue("categoryPath", categoryPath);
      setValue("market", market ?? getCategoryKeyMarket(responseCategoryKey));
      setValue("url", url);
      setValue("startDate", formatShopCategoryDate(startDate));
      setValue("endDate", formatShopCategoryDate(endDate));
      const criteriaValues = parseShopCriteriaToFormValues(shopCriteria);
      if (criteriaValues) {
        setValue("attributeCriteriaGroups", criteriaValues.attributeCriteriaGroups);
        setValue("includedWebstyles", criteriaValues.includedWebstyles);
        setValue("excludedWebstyles", criteriaValues.excludedWebstyles);
      } else {
        resetCriteriaFields();
      }
    };
    t6 = [debouncedCategoryKey, isLookupSettled, setValue, shopCategoryQuery.data, shopCategoryQuery.isError, shopCategoryQuery.isSuccess];
    $[8] = debouncedCategoryKey;
    $[9] = isLookupSettled;
    $[10] = setValue;
    $[11] = shopCategoryQuery.data;
    $[12] = shopCategoryQuery.isError;
    $[13] = shopCategoryQuery.isSuccess;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  const showDetails = isLookupSettled && shopCategoryQuery.isSuccess && Boolean(shopCategoryQuery.data?.automatedShop);
  let categoryKeyError = null;
  if (isLookupSettled) {
    if (shopCategoryQuery.error instanceof ShopCategoryNotFoundError) {
      categoryKeyError = "Invalid Category Key";
    } else {
      if (shopCategoryQuery.isSuccess && !shopCategoryQuery.data?.automatedShop) {
        categoryKeyError = "Automated Shop is not enabled for this category";
      }
    }
  }
  const apiError = isLookupSettled && shopCategoryQuery.error instanceof ShopCategoryApiError ? `API error (${shopCategoryQuery.error.status}). Please reenter category key and try again.` : null;
  let t7;
  bb0: {
    if (!shopCategoryQuery.data) {
      t7 = null;
      break bb0;
    }
    const {
      categoryPath: categoryPath_0,
      url: url_0,
      startDate: startDate_0,
      endDate: endDate_0,
      shopCriteria: shopCriteria_0
    } = shopCategoryQuery.data;
    let t8;
    if ($[16] !== startDate_0) {
      t8 = formatShopCategoryDate(startDate_0);
      $[16] = startDate_0;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== endDate_0) {
      t9 = formatShopCategoryDate(endDate_0);
      $[18] = endDate_0;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] !== categoryPath_0 || $[21] !== shopCriteria_0 || $[22] !== t8 || $[23] !== t9 || $[24] !== url_0) {
      t10 = {
        categoryPath: categoryPath_0,
        url: url_0,
        startDate: t8,
        endDate: t9,
        shopCriteria: shopCriteria_0
      };
      $[20] = categoryPath_0;
      $[21] = shopCriteria_0;
      $[22] = t8;
      $[23] = t9;
      $[24] = url_0;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    t7 = t10;
  }
  const lookupData = t7;
  const t8 = categoryKeyError;
  const t9 = Boolean(trimmedCategoryKey && (!isLookupSettled || shopCategoryQuery.isFetching));
  let t10;
  if ($[26] !== apiError || $[27] !== categoryKeyError || $[28] !== lookupData || $[29] !== showDetails || $[30] !== t9) {
    t10 = {
      lookupData,
      showDetails,
      categoryKeyError: t8,
      apiError,
      isLookupLoading: t9
    };
    $[26] = apiError;
    $[27] = categoryKeyError;
    $[28] = lookupData;
    $[29] = showDetails;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};